import { useMemo, useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Avatar,
  Button,
  EmptyState,
  GlassTable,
  ResponsiveDataTable,
  SearchInput,
  SkeletonList,
  StatusTabFilter,
} from '@jumpstone/ui-react';
import { ScreenHeader } from '../../components/common/screen_header';
import {
  count_employees_by_type,
  filter_employees,
  filter_employees_by_type,
  parse_employee_type_filter,
  resolve_employee_directory_view,
  sort_employees,
  toggle_direction,
} from './employee_directory_utils';
import type {
  EmployeeTypeFilter,
  SortDirection,
} from './employee_directory_utils';
import {
  EMPLOYMENT_STATUS_KEY,
  EMPLOYMENT_TYPE_KEY,
} from '../../lib/label_keys';
import { triggerDownload } from '../../lib/download';
import {
  EMPLOYEE_DIRECTORY_CSV_MIME,
  UTF8_BOM,
  build_employee_directory_csv,
  employee_directory_csv_filename,
} from './employee_directory_export';
import { useEmployees } from '../../data/use_employees';
import { useEmployeeMutations } from '../../data/use_employee_mutations';
import { useSettings } from '../../data/use_settings';
import { deriveJurisdiction } from './employee_form_validation';
import { EmployeeFormDialog } from './employee_form_dialog';
import { EmploymentStatusBadge } from './employment_status_badge';
import { EmploymentTypeBadge } from './employment_type_badge';
import { WorkerTypesBanner } from './worker_types_banner';
import {
  WORKER_TYPE_ORDER,
  worker_type_capabilities,
} from '../../lib/worker_types';
import { employment_type_label } from '../../lib/worker_type_labels';
import { useEssInvites } from '../../data/use_ess_invites';
import type { EssEmployeeStatus } from '../../data/wagepro_store_ess_invites';
import { useDirectoryEssActions } from './employee_directory_ess';
import {
  EmployeeEssActionButton,
  EmployeeEssStatusCell,
} from './employee_ess_cell';
import { DirectoryEssNoticeBanner } from './employee_directory_ess_notice';
import { EssInviteLinkDialog } from './ess_invite_link_dialog';
import { navigate } from '../../lib/router';
import { localise_fixture } from '../../data/mock_labels';
import type { Employee } from '../../data/types';

/**
 * Searchable, sortable list of everyone employed by the company.
 *
 * Renders each `useEmployees()` state distinctly (PR #22369 adversarial
 * review finding #1) — a genuine fetch-in-flight, a missing-`payroll`-
 * add-on `entitlement_required`, and a real fetch `error` each get their
 * own copy, never the generic "No employees match your search" empty state
 * that copy is reserved for a genuinely resolved, empty-after-filtering
 * directory. `resolve_employee_directory_view`
 * (`employee_directory_utils.ts`) is the pure, unit-tested source of truth
 * for that ordering.
 */
export function EmployeeDirectoryScreen({
  search_query,
  on_search_change,
}: {
  search_query: string;
  on_search_change: (value: string) => void;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const { employees, state, retry, pay_currencies } = useEmployees();
  const { create, is_read_only_grant } = useEmployeeMutations();
  const { organisation } = useSettings();
  const jurisdiction = deriveJurisdiction(organisation.base_currency);
  const [direction, set_direction] = useState<SortDirection>('asc');
  const [create_open, set_create_open] = useState(false);
  const [type_filter, set_type_filter] = useState<EmployeeTypeFilter>('all');
  // Self-service (WagePro ESS) status per row. Loaded alongside the directory
  // but never blocks it: until the invite list resolves the column shows a
  // dash, and a failed invite list only blanks that one column.
  const ess = useEssInvites();
  const ess_actions = useDirectoryEssActions(ess);
  const ess_status_of = (employee_id: string): EssEmployeeStatus | null =>
    ess.state.status === 'ready' ? ess.status_for_employee(employee_id) : null;

  // Search first, then count per type (so the tab counts describe what the
  // search matched), then narrow to the selected type and sort.
  const searched = useMemo(
    () => filter_employees(employees, search_query),
    [employees, search_query],
  );
  const type_counts = useMemo(
    () => count_employees_by_type(searched),
    [searched],
  );
  const rows = useMemo(
    () =>
      sort_employees(
        filter_employees_by_type(searched, type_filter),
        direction,
        locale,
      ),
    [searched, type_filter, direction, locale],
  );
  const type_tabs = [
    {
      id: 'all',
      label: t('employees.filter_type_all'),
      count: type_counts.all,
    },
    ...WORKER_TYPE_ORDER.map((type) => ({
      id: type,
      label: employment_type_label(t, type),
      count: type_counts[type],
    })),
  ];

  const view = resolve_employee_directory_view({
    state,
    filtered_row_count: rows.length,
  });

  /**
   * Download the rows currently on screen as CSV. The button carrying this
   * rendered with no `onClick` prop at all -- it was focusable, it looked
   * enabled, and pressing it did nothing.
   *
   * Uses the shared `lib/download.ts` side effect the payroll screens
   * already go through, rather than a second local implementation.
   */
  const export_directory = async (): Promise<void> => {
    const csv = build_employee_directory_csv(
      rows,
      {
        employee: t('employees.column_employee'),
        department: t('employees.column_department'),
        role: t('employees.column_role'),
        type: t('employees.column_type'),
        status: t('employees.column_status'),
      },
      {
        localise: (value) => localise_fixture(value, locale),
        employment_type: (type) => t(EMPLOYMENT_TYPE_KEY[type]),
        employment_status: (status) => t(EMPLOYMENT_STATUS_KEY[status]),
      },
    );
    await triggerDownload(
      new Blob([UTF8_BOM, csv], { type: EMPLOYEE_DIRECTORY_CSV_MIME }),
      employee_directory_csv_filename(new Date()),
    );
  };

  const open_profile = (employee_id: string): void => {
    navigate({
      module: 'employees',
      view: 'profile',
      employee_id,
      tab: 'job',
    });
  };

  return (
    <>
      <ScreenHeader
        title={t('employees.directory_title')}
        description={t('employees.directory_subtitle')}
        actions={
          <>
            {/* Disabled when there is nothing to export -- a loading,
                errored, entitlement-blocked or filtered-to-empty directory
                would otherwise hand the user a headers-only file. */}
            <Button
              variant="secondary"
              size="sm"
              disabled={rows.length === 0}
              onClick={() => {
                void export_directory();
              }}
            >
              {t('common.export')}
            </Button>
            {!is_read_only_grant && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  set_create_open(true);
                }}
              >
                {t('employees.add')}
              </Button>
            )}
          </>
        }
      />

      <EmployeeFormDialog
        open={create_open}
        onOpenChange={set_create_open}
        mode="create"
        initial={null}
        jurisdiction={jurisdiction}
        currency={organisation.base_currency}
        payCurrencies={pay_currencies}
        create={create}
        update={undefined}
        onSaved={retry}
        onCreated={
          is_read_only_grant
            ? undefined
            : (employee_id, options) => {
                void ess_actions.after_employee_created(employee_id, options);
              }
        }
      />

      <EssInviteLinkDialog
        link={ess_actions.fallback_link}
        open={ess_actions.fallback_link !== null}
        onOpenChange={(next) => {
          if (!next) ess_actions.close_fallback_link();
        }}
      />

      <WorkerTypesBanner />

      {/* The directory is the only searchable screen, so it owns the search
          box at every width (there is no global one in `TopNav`). Hidden
          until there is someone to search -- an empty company gets the
          "No employees yet" state below instead. */}
      {state.status === 'ready' && employees.length > 0 && (
        <div role="search" className="mb-3 max-w-sm">
          <SearchInput
            value={search_query}
            onChange={on_search_change}
            placeholder={t('nav.search_placeholder')}
            showKbd={false}
          />
        </div>
      )}

      {state.status === 'ready' && employees.length > 0 && (
        <div
          role="group"
          aria-label={t('employees.filter_type_label')}
          className="mb-3"
        >
          <StatusTabFilter
            tabs={type_tabs}
            activeTab={type_filter}
            onChange={(id) => {
              set_type_filter(parse_employee_type_filter(id));
            }}
          />
        </div>
      )}

      {ess_actions.notice !== null && (
        <DirectoryEssNoticeBanner
          notice={ess_actions.notice}
          onDismiss={ess_actions.dismiss_notice}
        />
      )}

      {view === 'loading' && (
        <GlassTable>
          <div className="p-4">
            <span className="sr-only">{t('employees.directory_loading')}</span>
            <SkeletonList rows={5} />
          </div>
        </GlassTable>
      )}

      {view === 'no_entity' && (
        <EmptyState
          title={t('employees.directory_no_entity_title')}
          description={t('employees.directory_no_entity_description')}
        />
      )}

      {view === 'entitlement_required' && (
        <EmptyState
          title={t('employees.directory_entitlement_required_title')}
          description={t(
            'employees.directory_entitlement_required_description',
          )}
        />
      )}

      {view === 'error' && (
        <EmptyState
          title={t('employees.directory_error_title')}
          description={t('employees.directory_error_description')}
          action={
            <Button variant="secondary" onClick={retry}>
              {t('employees.directory_retry')}
            </Button>
          }
        />
      )}

      {view === 'no_employees' && (
        <EmptyState
          title={t('employees.no_employees_title')}
          description={t('employees.no_employees_description')}
          action={
            is_read_only_grant ? undefined : (
              <Button
                variant="primary"
                onClick={() => {
                  set_create_open(true);
                }}
              >
                {t('employees.add')}
              </Button>
            )
          }
        />
      )}

      {view === 'empty' && (
        <EmptyState
          title={t('employees.empty_title')}
          description={t('employees.empty_description')}
        />
      )}

      {view === 'rows' && (
        <ResponsiveDataTable<Employee>
          data-testid="employee-directory-table"
          rows={rows}
          rowKey={(employee) => employee.id}
          // Below `md` the desktop `<table>` (which needs horizontal scroll
          // to reach the last "View" column at 360px) is replaced by a card
          // per employee -- see `mobileCard` below.
          mobileCard={(employee) => (
            // The whole card is the tap target (opens the profile) --
            // replaces a separate "View" affordance, which read as
            // disconnected from the row no matter where it was placed
            // (its own floating footer row; crammed inline next to the
            // status badge, which then truncated the name). A native
            // `<button>` gets real keyboard/focus semantics for free, so
            // the name itself no longer needs to be its own nested
            // `<Button>` -- a `<button>` inside a `<button>` isn't valid
            // HTML, and one tap target for the whole row is simpler than
            // two overlapping ones.
            //
            // `aria-label` is required, not cosmetic: a `<button>`'s
            // accessible name is computed from its ENTIRE subtree absent
            // one, which now includes the avatar's initials, the status
            // badge's label, and all three department/role/type
            // `<dt>`/`<dd>` pairs -- a screen reader would announce one
            // run-on string ("NP Nadeesha Perera Active Department
            // Engineering Role...") instead of the employee's name. The
            // explicit label short-circuits that computation to exactly
            // what a "view this person's profile" control should announce.
            <button
              type="button"
              aria-label={t('employees.open_profile_for', {
                name: employee.name,
              })}
              className="w-full text-left p-4 flex flex-col gap-2.5"
              onClick={() => {
                open_profile(employee.id);
              }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-2.5 min-w-0">
                  <Avatar
                    name={employee.name}
                    colorVariant={employee.tint}
                    size="sm"
                  />
                  <span className="font-semibold truncate">
                    {employee.name}
                  </span>
                </span>
                <span className="flex items-center gap-1.5 shrink-0">
                  <EmploymentStatusBadge status={employee.status} />
                  <ChevronRightIcon className="h-4 w-4 text-[color:var(--ink-muted)]" />
                </span>
              </div>
              <dl className="grid grid-cols-3 gap-x-2 text-[12.5px]">
                <div className="min-w-0">
                  <dt className="text-[11px] text-[color:var(--ink-muted)]">
                    {t('employees.column_department')}
                  </dt>
                  <dd className="truncate">
                    {localise_fixture(employee.department, locale)}
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-[11px] text-[color:var(--ink-muted)]">
                    {t('employees.column_role')}
                  </dt>
                  <dd className="truncate">
                    {localise_fixture(employee.job_title, locale)}
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt className="text-[11px] text-[color:var(--ink-muted)]">
                    {t('employees.column_type')}
                  </dt>
                  <dd className="truncate">
                    <EmploymentTypeBadge type={employee.employment_type} />
                  </dd>
                </div>
              </dl>
              {/* Status only -- the whole card is one tap target, so the
                  invite action lives on the profile's access tab here. */}
              <span className="flex items-center gap-2 text-[12.5px]">
                <span className="text-[11px] text-[color:var(--ink-muted)]">
                  {t('employees.column_self_service')}
                </span>
                <EmployeeEssStatusCell
                  status={ess_status_of(employee.id)}
                  invite={ess.invite_for_employee(employee.id)}
                />
              </span>
            </button>
          )}
          // Desktop leg kept byte-identical to the pre-adoption markup
          // (custom sortable header button + GlassTable wrapper) via the
          // `renderDesktop` escape hatch -- see responsive_data_table.tsx.
          renderDesktop={(rows) => (
            <GlassTable>
              <table className="nv-table">
                <thead>
                  <tr>
                    <th scope="col">
                      <button
                        type="button"
                        className="wp-sort-button"
                        onClick={() => {
                          set_direction(toggle_direction(direction));
                        }}
                        aria-label={t('employees.sort_by_name')}
                      >
                        {t('employees.column_employee')}{' '}
                        <span aria-hidden="true">
                          {direction === 'asc' ? '↑' : '↓'}
                        </span>
                      </button>
                    </th>
                    <th scope="col">{t('employees.column_department')}</th>
                    <th scope="col">{t('employees.column_role')}</th>
                    <th scope="col">{t('employees.column_type')}</th>
                    <th scope="col">{t('employees.column_status')}</th>
                    <th scope="col">{t('employees.column_self_service')}</th>
                    <th scope="col">
                      <span className="sr-only">
                        {t('employees.column_self_service_actions')}
                      </span>
                    </th>
                    <th scope="col">
                      <span className="sr-only">{t('common.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((employee) => (
                    <tr key={employee.id}>
                      <td>
                        <span className="flex items-center gap-2.5">
                          <Avatar
                            name={employee.name}
                            colorVariant={employee.tint}
                            size="sm"
                          />
                          <Button
                            variant="ghost"
                            size="xs"
                            onClick={() => {
                              open_profile(employee.id);
                            }}
                          >
                            {employee.name}
                          </Button>
                        </span>
                      </td>
                      <td>{localise_fixture(employee.department, locale)}</td>
                      <td>{localise_fixture(employee.job_title, locale)}</td>
                      <td>
                        <EmploymentTypeBadge type={employee.employment_type} />
                      </td>
                      <td>
                        <EmploymentStatusBadge status={employee.status} />
                      </td>
                      <td>
                        <EmployeeEssStatusCell
                          status={ess_status_of(employee.id)}
                          invite={ess.invite_for_employee(employee.id)}
                        />
                      </td>
                      {/* Invite action in its own column so every row's
                          button starts at the same edge, whatever the width
                          of the status badge beside it. */}
                      <td>
                        <EmployeeEssActionButton
                          status={ess_status_of(employee.id)}
                          busy={ess_actions.busy_employee_id === employee.id}
                          disabled={
                            !worker_type_capabilities(employee.employment_type)
                              .ess ||
                            (ess_actions.busy_employee_id !== null &&
                              ess_actions.busy_employee_id !== employee.id)
                          }
                          onSendInvite={
                            ess_actions.send_invite
                              ? () => {
                                  void ess_actions.send_invite?.(employee.id);
                                }
                              : undefined
                          }
                        />
                      </td>
                      <td className="text-end">
                        <Button
                          variant="secondary"
                          size="xs"
                          onClick={() => {
                            open_profile(employee.id);
                          }}
                        >
                          {t('common.view')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </GlassTable>
          )}
        />
      )}
    </>
  );
}

/** Row-is-tappable affordance for the mobile employee card -- signals the
 * whole card opens the profile, same role a "View" button used to play. */
function ChevronRightIcon({
  className,
}: {
  className?: string;
}): React.JSX.Element {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}
